import { FullStory, init, isInitialized } from '@fullstory/browser';
import { AccountDefinition, Features, ModuleNames } from '../../../common/model/account-definition';
import { TrialTrackingEnvironment } from '../../../../environments/trial-tracking-config';

type FullStoryLogLevel = 'log' | 'warn' | 'error' | 'debug';

export class FullStoryService {
  private static _environment: TrialTrackingEnvironment;

  public static initialize(orgId: string, environment: TrialTrackingEnvironment): void {
    FullStoryService._environment = environment;
    if (isInitialized()) return;

    // The org is in the EU region. All three settings are required: script is a host and path, not
    // merely a hostname. Using the bare host produces an XML response that cannot execute as script.
    init({
      orgId,
      host: 'eu1.fullstory.com',
      script: 'edge.eu1.fullstory.com/s/fs.js',
      appHost: 'app.eu1.fullstory.com'
    });
  }

  public static identifyUser(userContext: AccountDefinition): void {
    if (!isInitialized()) return;

    FullStory('setIdentity', {
      uid: userContext.id,
      properties: FullStoryService.userProperties(userContext)
    });
  }

  public static updateUserProperties(userContext: AccountDefinition): void {
    if (!isInitialized()) return;

    FullStory('setProperties', {
      type: 'user',
      properties: FullStoryService.userProperties(userContext)
    });
  }

  public static track(eventName: string, properties?: Record<string, unknown>): void {
    if (!isInitialized()) return;

    FullStory('trackEvent', { name: eventName, properties: properties ?? {} });
  }

  public static log(msg: string, level: FullStoryLogLevel = 'log'): void {
    if (!isInitialized()) return;

    FullStory('log', { level, msg });
  }

  public static sessionUrl(): string | null {
    if (!isInitialized()) return null;

    return FullStory('getSession', { format: 'url' }) ?? null;
  }

  public static resetUser(): void {
    if (!isInitialized()) return;

    FullStory('setIdentity', { anonymous: true });
  }

  private static userProperties(userContext: AccountDefinition): Record<string, unknown> {
    // displayName and email are deliberately omitted. FullStory v2 infers types and rejects the old
    // type-suffixed property names.
    return {
      environment: FullStoryService._environment,
      is_internal: FullStoryService.isInternalEmail(userContext.email),
      can_manage_users: userContext.hasAccess(ModuleNames.System, Features.Admin),
      can_manage_billing: userContext.hasAccess(ModuleNames.Invoicing, Features.ManageInvoices),
      can_see_customer_account: userContext.hasAccess(ModuleNames.System, Features.Overview),
      has_any_permission: userContext.modules.length > 0 || userContext.features.length > 0,
      language: FullStoryService.currentLanguage(),
      customer_id: userContext.systemOwnerId,
      customer_name: userContext.name
    };
  }

  private static isInternalEmail(email: string): boolean {
    const domain = email?.toLowerCase().split('@')[1];
    return !!domain && domain.includes('hogia');
  }

  private static currentLanguage(): string {
    return localStorage.getItem('lang') ?? 'sv';
  }
}
